import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Users, Store, Plus, Settings, ToggleLeft, ToggleRight, LogOut, ChevronRight, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_FLAGS = [
  { name: 'TABLE_SERVICE', description: 'Enable table service mode' },
  { name: 'QUICK_SERVICE', description: 'Enable quick service mode' },
  { name: 'SPLIT_BILL', description: 'Allow splitting bills' },
  { name: 'DISCOUNTS', description: 'Enable discounts' },
  { name: 'MANAGER_OVERRIDE', description: 'Require manager PIN for overrides' },
  { name: 'VOID_REFUND', description: 'Allow voids and refunds' },
  { name: 'CASH_IN_OUT', description: 'Enable cash-in/out operations' },
  { name: 'DEPOSITS', description: 'Enable deposit tracking' },
  { name: 'XZ_READING_LOCK', description: 'Lock shifts with Z-Reading' },
  { name: 'OFFLINE_MODE', description: 'Enable offline POS' },
  { name: 'INVENTORY_CORE', description: 'Core inventory features' },
  { name: 'PURCHASE_ORDERS', description: 'Purchase order management' },
  { name: 'RECEIVING', description: 'Goods receiving' },
  { name: 'TRANSFERS', description: 'Stock transfers' },
  { name: 'RECIPE_BOM', description: 'Recipe/BOM management' },
  { name: 'COST_CONTROL_REPORTS', description: 'Cost control reports' },
  { name: 'CRM', description: 'Customer relationship management' },
  { name: 'LOYALTY', description: 'Loyalty program' },
  { name: 'PROMOTIONS', description: 'Promotions engine' },
  { name: 'ONLINE_PAYMENTS', description: 'Online payment integrations' },
];

export default function PlatformAdminPage() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showFeatureFlags, setShowFeatureFlags] = useState(false);
  const [tenantFlags, setTenantFlags] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [newTenant, setNewTenant] = useState({ name: '', slug: '', admin_email: '', admin_password: '', admin_name: '' });

  const fetchData = async () => {
    try {
      const [tenantsRes] = await Promise.all([
        fetch(`${API_URL}/api/platform/admin/tenants`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (tenantsRes.ok) {
        const data = await tenantsRes.json();
        setTenants(data.items || []);
        // Calculate stats from tenants
        setStats({
          total_tenants: data.total || 0,
          total_users: (data.items || []).reduce((sum, t) => sum + (t.users_count || 0), 0),
          total_outlets: (data.items || []).reduce((sum, t) => sum + (t.outlets_count || 0), 0)
        });
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const createTenant = async () => {
    if (!newTenant.name || !newTenant.slug || !newTenant.admin_email || !newTenant.admin_password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/platform/admin/tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: newTenant.name,
          slug: newTenant.slug,
          owner_email: newTenant.admin_email,
          owner_password: newTenant.admin_password,
          owner_name: newTenant.admin_name || newTenant.name,
          plan_tier: 'starter'
        })
      });
      
      if (res.ok) {
        toast.success('Tenant created');
        setShowCreateTenant(false);
        setNewTenant({ name: '', slug: '', admin_email: '', admin_password: '', admin_name: '' });
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to create tenant');
      }
    } catch (err) {
      toast.error('Failed to create tenant');
    }
  };

  const toggleTenant = async (tenantId, isActive) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/admin/tenants/${tenantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: !isActive })
      });
      if (res.ok) {
        toast.success('Tenant status updated');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to update tenant');
    }
  };

  const openFeatureFlags = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const res = await fetch(`${API_URL}/api/platform/admin/tenants/${tenant.id}/feature-flags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Transform the response to a simple object
        const flags = {};
        Object.entries(data).forEach(([key, value]) => {
          flags[key] = value.enabled;
        });
        setTenantFlags(flags);
        setShowFeatureFlags(true);
      }
    } catch (err) {
      toast.error('Failed to load feature flags');
    }
  };

  const toggleFlag = async (flagName, isEnabled) => {
    try {
      await fetch(`${API_URL}/api/platform/admin/tenants/${selectedTenant.id}/feature-flags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ flag_name: flagName, is_enabled: isEnabled, config: {} })
      });
      setTenantFlags(prev => ({ ...prev, [flagName]: isEnabled }));
      toast.success(`${flagName} ${isEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update flag');
    }
  };

  const filteredTenants = tenants.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="platform-admin-page">
      {/* Header */}
      <header className="h-16 bg-gray-800 border-b border-gray-700 flex items-center px-6">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Settings className="w-6 h-6 text-teal-400" />
          BENTA — Platform Admin
        </h1>
        <div className="flex-1" />
        <Button variant="ghost" onClick={handleLogout}>
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </header>

      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Total Tenants
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-teal-400">{stats.total_tenants}</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Total Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{stats.total_users}</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
                  <Store className="w-4 h-4" /> Total Outlets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{stats.total_outlets}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tenants */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Tenants</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search tenants..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                       className="pl-10 bg-gray-700 border-gray-600 w-64" />
              </div>
              <Button onClick={() => setShowCreateTenant(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="create-tenant-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Tenant
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto"><table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="pb-3">Tenant</th>
                  <th className="pb-3">Slug</th>
                  <th className="pb-3">Plan</th>
                  <th className="pb-3 text-center">Users</th>
                  <th className="pb-3 text-center">Outlets</th>
                  <th className="pb-3 text-center">Status</th>
                  <th className="pb-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.map(tenant => (
                  <tr key={tenant.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="py-3 font-medium">{tenant.name}</td>
                    <td className="py-3 text-gray-400">{tenant.slug}</td>
                    <td className="py-3">{tenant.plan_name || '-'}</td>
                    <td className="py-3 text-center">{tenant.user_count}</td>
                    <td className="py-3 text-center">{tenant.outlet_count}</td>
                    <td className="py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${tenant.is_active ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>
                        {tenant.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openFeatureFlags(tenant)} data-testid={`flags-${tenant.slug}`}>
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleTenant(tenant.id, tenant.is_active)}>
                          {tenant.is_active ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </CardContent>
        </Card>
      </div>

      {/* Create Tenant Modal */}
      <Dialog open={showCreateTenant} onOpenChange={setShowCreateTenant}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400">Restaurant Name *</label>
              <Input value={newTenant.name} onChange={e => setNewTenant(prev => ({ ...prev, name: e.target.value }))}
                     placeholder="e.g., ABC Restaurant" className="bg-gray-700 border-gray-600" />
            </div>
            <div>
              <label className="text-sm text-gray-400">Slug (URL-friendly) *</label>
              <Input value={newTenant.slug} onChange={e => setNewTenant(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s/g, '-') }))}
                     placeholder="e.g., abc-restaurant" className="bg-gray-700 border-gray-600" />
            </div>
            <div>
              <label className="text-sm text-gray-400">Admin Name</label>
              <Input value={newTenant.admin_name} onChange={e => setNewTenant(prev => ({ ...prev, admin_name: e.target.value }))}
                     placeholder="Admin user name" className="bg-gray-700 border-gray-600" />
            </div>
            <div>
              <label className="text-sm text-gray-400">Admin Email *</label>
              <Input type="email" value={newTenant.admin_email} onChange={e => setNewTenant(prev => ({ ...prev, admin_email: e.target.value }))}
                     placeholder="admin@restaurant.com" className="bg-gray-700 border-gray-600" />
            </div>
            <div>
              <label className="text-sm text-gray-400">Admin Password *</label>
              <Input type="password" value={newTenant.admin_password} onChange={e => setNewTenant(prev => ({ ...prev, admin_password: e.target.value }))}
                     placeholder="••••••••" className="bg-gray-700 border-gray-600" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTenant(false)}>Cancel</Button>
            <Button onClick={createTenant} className="bg-teal-600 hover:bg-teal-700">Create Tenant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feature Flags Modal */}
      <Dialog open={showFeatureFlags} onOpenChange={setShowFeatureFlags}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-lg" data-testid="feature-flags-modal">
          <DialogHeader>
            <DialogTitle>Feature Flags - {selectedTenant?.name}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-96">
            <div className="space-y-3">
              {ALL_FLAGS.map(flag => (
                <div key={flag.name} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="font-medium">{flag.name}</p>
                    <p className="text-xs text-gray-400">{flag.description}</p>
                  </div>
                  <Switch checked={tenantFlags[flag.name] || false} onCheckedChange={(checked) => toggleFlag(flag.name, checked)}
                          data-testid={`flag-${flag.name.toLowerCase()}`} />
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeatureFlags(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
